import { useLang } from 'hooks/lang';

import { loaders, User } from 'entities';

import { UserAvatar } from 'components/UserAvatar';
import { PlusIcon, PencilIcon } from 'components/Icons';
import { Button, Table, Row, TitledCard } from 'components/Blocks';

import UserStatus from './Status';
import UserScreen from './UserScreen';

import { useIsBreakpoint, useToggleV2 } from 'hooks';

import { useTeamProfile } from '../context';
import { useFocusedUser } from './focusedUser.hook';

interface RefreshParams {
  mutatedUser: User;
  isUpdate: boolean;
}

const initialUser = {
  name: '',
  email: '',
  office: '',
  type: 'USER',
  language: '',
} as unknown as User;

function Users() {
  const { t } = useLang();
  const isMobile = useIsBreakpoint('PHONE');

  const [isCreating, creatingHandlers] = useToggleV2();

  const { focusId, focusUser } = useFocusedUser();

  const { team, canMutateEntities } = useTeamProfile();

  const users = loaders.users.teamUsers.use({
    defaultValue: [],

    params: { team: team.id },
  });

  const refreshUsers = ({ mutatedUser, isUpdate }: RefreshParams): void => {
    const newData = isUpdate
      ? users.data.map((user) => (user.id === mutatedUser.id ? mutatedUser : user))
      : [...users.data, mutatedUser];

    users.overwrite(newData);
  };

  const deleteUser = (id: string): void => {
    const newData = users.data.filter((user) => user.id !== id);

    users.overwrite(newData);
  };

  const focusedUser = isCreating ? initialUser : users.data.find(({ id }) => id === focusId);

  const unfocusUser = () => {
    creatingHandlers.turnOff();
    focusUser(null);
  };

  if (focusedUser)
    return (
      <UserScreen
        user={focusedUser}
        goBack={unfocusUser}
        refreshUsers={refreshUsers}
        deleteUser={() => deleteUser(focusedUser.id)}
      />
    );

  return (
    <TitledCard
      title={t('users')}
      headerRightContent={
        canMutateEntities && (
          <Button variant="ghost" onClick={creatingHandlers.turnOn}>
            <PlusIcon />
            {t('create')}
          </Button>
        )
      }
    >
      <Table
        items={users.data}
        uniqueKey="id"
        displayNoItemMessage
        loading={users.loading}
        spacing="medium"
        columns={[
          {
            render: (user) => <UserAvatar user={user} />,
            className: 'w-[70px]',
          },
          {
            property: 'name',
            label: t('name')!,
            search: true,
            render: ({ name, id }) => (
              <Button
                variant="link"
                color="gray"
                className="font-normal"
                onClick={() => focusUser(id)}
              >
                {name}
              </Button>
            ),
          },
          {
            label: t('type')!,
            filter: true,
            render: ({ type }) => t(type),
          },
          {
            property: 'email',
            label: t('email')!,
            search: true,
          },
          {
            render: (user) => (
              <Row justifyContent={isMobile ? 'center' : 'flex-end'}>
                <UserStatus user={user} updateUsers={users.overwrite} />

                {canMutateEntities && (
                  <Button variant="ghost" onClick={() => focusUser(user.id)}>
                    <PencilIcon size={16} />
                    {t('manage')}
                  </Button>
                )}
              </Row>
            ),
          },
        ]}
      />
    </TitledCard>
  );
}

export default Users;
